import axios from "axios";
import React, { useEffect, useRef, useState } from "react";
import {
  Paper,
  Container,
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Pagination,
  Typography,
  TextField,
  InputAdornment,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import {
  Add as AddIcon,
  Close,
  Edit as EditIcon,
  Search,
} from "@mui/icons-material";
import { Loading } from "../../../components/Loading";
import NavigationHeader from "../../../components/NavigationHeader";
import { getValidToken } from "../../../utils/authUtils";
import {
  tableCellStyles,
  tableHeaderStyles,
  tableRowStyles,
  commonTextFieldProps,
} from "../../../utils/styles";
import { formatCurrency } from "../../../utils/utils";

interface ServiceOperator {
  id: string;
  companyId: string;
  name: string;
  contactNumber: string;
  contactPerson: string;
  address: string;
  gstNumber: string | null;
  status: "ACTIVE" | "INACTIVE" | "BLACKLISTED";
  notes: string;
  type: "INHOUSE" | "EXTERNAL";
  operationType: "BUS" | "LUGGAGE" | "OTHER";
  pricingType: "Percentage" | "Fixed";
  pricingValue: number;
}

const fetchServiceOperators = async (page: number, search?: string) => {
  try {
    const token = await getValidToken();
    const queryParams = new URLSearchParams({
      page: page.toString(),
      limit: "20",
      ...(search && search.trim() ? { search: search.trim() } : {}),
    });
    const url = `${process.env.REACT_APP_API_HOST}service-operators?${queryParams}`;
    const response = await axios.get(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    if (response.data?.isSuccess) {
      return {
        data: response.data?.data,
        pagination: response.data?.pagination || { pages: 1 },
      };
    } else {
      return { data: [], pagination: { pages: 1 } };
    }
  } catch (error) {
    return { data: [], pagination: { pages: 1 } };
  }
};

const ServiceOperators = () => {
  const [serviceOperators, setServiceOperators] = useState<ServiceOperator[]>(
    []
  );
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const debounceTimerRef = useRef<number | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    loadOperators();
  }, [page, debouncedSearch]);

  // Debounce the search input but allow manual immediate triggers
  useEffect(() => {
    if (debounceTimerRef.current) {
      window.clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = window.setTimeout(() => {
      const term = searchQuery.trim();
      setDebouncedSearch(term.length >= 2 ? term : "");
    }, 300);
    return () => {
      if (debounceTimerRef.current) {
        window.clearTimeout(debounceTimerRef.current);
      }
    };
  }, [searchQuery]);

  const loadOperators = async (
    pageOverride?: number,
    searchOverride?: string
  ) => {
    try {
      setIsLoading(true);
      const effectivePage = pageOverride ?? page;
      const effectiveSearch = searchOverride ?? searchQuery;
      const { data, pagination } = await fetchServiceOperators(
        effectivePage,
        effectiveSearch
      );
      setServiceOperators(data);
      setTotalPages(pagination?.pages || 1);
    } catch (error) {
      setServiceOperators([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOperatorClick = (operatorId: string) => {
    navigate(`/master-data/operators/${operatorId}`);
  };

  return (
    <Container maxWidth="lg" sx={{ py: 2 }}>
      <NavigationHeader
        breadcrumbs={[
          { label: "Master Data", path: "/master-data" },
          { label: "Operators/Agents" },
        ]}
        title="Operators/Agents"
        button={{
          label: "Add Operator/Agent",
          onClick: () => navigate("/master-data/operators/new"),
          icon: <AddIcon />,
        }}
      />

      <>
        <Box
          sx={{
            mb: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            flexWrap: "wrap",
          }}
        >
          <TextField
            fullWidth
            label="Search by name, phone, or type"
            placeholder="Search by name, phone, or type..."
            value={searchQuery}
            onChange={(e) => {
              const value = e.target.value;
              setSearchQuery(value);
              setPage(1);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                const term = searchQuery.trim();
                const effective = term.length >= 2 ? term : "";
                setDebouncedSearch(effective);
                loadOperators(1, effective);
              }
            }}
            onBlur={() => {
              const term = searchQuery.trim();
              const effective = term.length >= 2 ? term : "";
              setDebouncedSearch(effective);
              loadOperators(1, effective);
            }}
            {...commonTextFieldProps}
            InputProps={{
              endAdornment: searchQuery.length > 0 && (
                <InputAdornment position="end">
                  <Close
                    style={{ fontSize: "1rem", cursor: "pointer" }}
                    onClick={() => {
                      setSearchQuery("");
                      setDebouncedSearch("");
                      setPage(1);
                      loadOperators(1, "");
                    }}
                  />
                </InputAdornment>
              ),
              startAdornment: (
                <Search style={{ fontSize: "1rem", marginRight: 2 }} />
              ),
            }}
          />
          {searchQuery && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontSize: "0.7rem" }}
            >
              {searchQuery.trim().length < 2
                ? "Type at least 2 characters"
                : `Showing ${serviceOperators.length} operators`}
            </Typography>
          )}
        </Box>

        <TableContainer component={Paper}>
          <Table aria-label="Operators table" size="small">
            <TableHead>
              <TableRow>
                <TableCell {...tableHeaderStyles}>Name</TableCell>
                <TableCell {...tableHeaderStyles}>Type</TableCell>
                <TableCell {...tableHeaderStyles}>Operation Type</TableCell>
                <TableCell {...tableHeaderStyles}>Status</TableCell>
                <TableCell {...tableHeaderStyles}>Contact Details</TableCell>
                <TableCell {...tableHeaderStyles}>Commission</TableCell>
                <TableCell {...tableHeaderStyles} align="center">
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading && (
                <TableRow>
                  <TableCell colSpan={6} sx={{ textAlign: "center" }}>
                    <Loading message="Loading operators..." fullScreen={false} />
                  </TableCell>
                </TableRow>
              )}
              {serviceOperators.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} sx={{ textAlign: "center" }}>
                    No operators found
                  </TableCell>
                </TableRow>
              ) : (
                serviceOperators.map((operator) => (
                  <TableRow key={operator.id} sx={{ ...tableRowStyles }}>
                    <TableCell
                      component="th"
                      scope="row"
                      sx={{ ...tableCellStyles }}
                    >
                      {operator.name}
                    </TableCell>
                    <TableCell sx={{ ...tableCellStyles }}>
                      {operator.type}
                    </TableCell>
                    <TableCell sx={{ ...tableCellStyles }}>
                      {operator.operationType}
                    </TableCell>
                    <TableCell sx={{ ...tableCellStyles }}>
                      <Chip
                        label={operator.status}
                        color={
                          operator.status === "ACTIVE"
                            ? "success"
                            : operator.status === "INACTIVE"
                            ? "default"
                            : "error"
                        }
                        size="small"
                        sx={{
                          fontSize: "0.6rem",
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ ...tableCellStyles }}>
                      {operator.contactPerson} - {operator.contactNumber}
                    </TableCell>
                    <TableCell sx={{ ...tableCellStyles }}>
                      {operator.pricingType === "Percentage"
                        ? `${operator.pricingValue}%`
                        : operator.pricingType === "Fixed"
                        ? `${formatCurrency(operator.pricingValue)}/item`
                        : "-"}
                    </TableCell>
                    <TableCell sx={{ ...tableCellStyles }} align="center">
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={() => handleOperatorClick(operator.id)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        {totalPages > 1 && (
          <Box display="flex" justifyContent="center" mt={4}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(_, value) => setPage(value)}
              color="primary"
            />
          </Box>
        )}
      </>
    </Container>
  );
};

export default ServiceOperators;
